import { __awaiter } from "tslib";
import { decrementOpenRequests, incrementOpenRequests, } from "../reducers/actions";
import { getSelectors, getSessionId } from "../reducers/selectors";
import { handleCfgStateResponse } from "./handleCfgStateResponse";
const fetchFactory = (options) => (dispatch_1, getState_1, _a) => __awaiter(void 0, [dispatch_1, getState_1, _a], void 0, function* (dispatch, getState, { cfgApi }) {
    var _b;
    try {
        // the needs to be done before throwing any errors since decrementOpenRequests is called in finally
        dispatch(incrementOpenRequests());
        const rootState = getState();
        const selectors = getSelectors(rootState);
        const sessionId = getSessionId(rootState);
        // event when using the AbortController, this still can happen from time to time
        if (!sessionId) {
            const message = [
                `fetchConfiguration triggered but sessionId is empty.`,
                `This issue might be caused be useEffect unmounting in StrictMode or debounced fetching`,
            ].join(" ");
            throw new Error(message);
        }
        const response = yield cfgApi.getConfiguration({
            id: sessionId,
            getConfigurationParams: {
                selectors,
            },
        }, { signal: options.abortSignal });
        dispatch(handleCfgStateResponse(response));
    }
    catch (err) {
        // do not rethrow error if the request ws aborted
        if (!((_b = options.abortSignal) === null || _b === void 0 ? void 0 : _b.aborted)) {
            throw err;
        }
    }
    finally {
        dispatch(decrementOpenRequests());
    }
});
/**
 * Refetch a configuration
 *
 * This can be necessary when selectors change and no other request is triggered
 *
 * @export
 * @param {GetCfgStateRequest} options
 * @returns {EcsThunkAction<Promise<void>>}
 */
export function fetchConfiguration(options) {
    return fetchFactory(options);
}
let fetchTimeout = 0;
/**
 * Refetch a configuration debounced
 *
 * - This can be necessary when selectors change and no other request is triggered
 *
 * @private
 * @param {GetCfgStateRequest} options
 * @returns {EcsThunkAction<Promise<void>>}
 */
export function fetchConfigurationDebounced(options) {
    return (dispatch_1, getState_1, _a) => __awaiter(this, [dispatch_1, getState_1, _a], void 0, function* (dispatch, getState, { cfgApi, eventEmitter }) {
        return new Promise((resolve, reject) => {
            const abortController = options.abortController;
            const abortSignal = options.abortController.signal;
            function clearDebounce() {
                // when the signal is aborted, clear the timer
                if (fetchTimeout) {
                    window.clearTimeout(fetchTimeout);
                    fetchTimeout = 0;
                }
            }
            function onAbort() {
                clearDebounce();
                resolve();
            }
            function onTimeout() {
                return __awaiter(this, void 0, void 0, function* () {
                    try {
                        yield fetchFactory({ abortSignal })(dispatch, getState, {
                            cfgApi,
                            eventEmitter,
                        });
                        resolve();
                    }
                    catch (err) {
                        if (abortSignal.aborted) {
                            resolve();
                        }
                        else {
                            reject(err);
                        }
                    }
                    finally {
                        abortSignal.removeEventListener("abort", onAbort);
                    }
                });
            }
            if (fetchTimeout) {
                // clear previous attempt: cancels timeout and aborts fetch
                abortController.abort();
            }
            // add listener to abort signal, and clear abort when done
            abortSignal.addEventListener("abort", onAbort, { once: true });
            // add listener to timeout
            fetchTimeout = window.setTimeout(onTimeout, 25);
        });
    });
}
//# sourceMappingURL=fetchConfiguration.js.map